import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../_helpers/axios_instance";

// --- Initial State ---
// Load currentGroup from localStorage
let currentGroupFromStorage = null;
try {
  const item = localStorage.getItem('currentGroup');
  currentGroupFromStorage = item ? JSON.parse(item) : null;
} catch (error) {
  console.error("Error parsing currentGroup from localStorage:", error);
  // Optionally clear the invalid item
  localStorage.removeItem('currentGroup');
}

const initialState = {
  groups: [],
  fetchingGroups: false,
  groupsFetchError: null,
  currentGroup: currentGroupFromStorage, //
};

// --- Async Thunks ---

/**
 * Fetches the list of practice groups from the API.
 * It avoids fetching if already in progress.
 * If only one group is returned and no group is currently selected, it auto-selects that group.
 */
export const getGroups = createAsyncThunk(
  "groups/getGroups",
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/api/practice_groups`
      );

      const fetchedGroups = response.data.data
        ? response.data.data.filter((group) => group.enabled) //
        : response.data.filter((group) => group.enabled); //

      // Auto-select group if only one exists and no currentGroup in localStorage
      // Check against localStorage directly because the state might not be updated yet if this is the initial load.
      if (fetchedGroups && fetchedGroups.length === 1 && localStorage.getItem("currentGroup") === null) {
        // Dispatch selectGroup thunk to handle localStorage and state update
        dispatch(selectGroup(fetchedGroups[0]));
      }
      return fetchedGroups;
    } catch (error) {
      console.error("ERROR FETCHING GROUPS", error); //
      if (error.response) {
        console.log("ERROR WITH RESPONSE", error.response); //
        const errorData = error.response.data;
        return rejectWithValue(errorData.errors || "Failed to fetch groups"); //
      } else if (error.request) {
        console.log("ERROR WITHOUT RESPONSE", error.request); //
        return rejectWithValue(error.message || "Network error, no response received"); //
      } else {
        console.log("Error setting up request for Groups Data", error.message); //
        return rejectWithValue(error.message || "Error in request setup"); //
      }
    }
  },
  {
    // Condition to prevent running if already fetching
    condition: (_, { getState }) => {
      const { groups } = getState();
      if (groups.fetchingGroups) {
        return false;
      }
    },
  }
);

/**
 * Selects a group and saves it to localStorage.
 */
export const selectGroup = createAsyncThunk(
  "groups/selectGroup",
  async (group, { dispatch }) => {
    if (group && typeof group === 'object') {
      localStorage.setItem('currentGroup', JSON.stringify(group)); //
      return group;
    }
    // If group is invalid, it might be better to throw an error or return a specific failure value.
    // For now, mimicking original behavior where the reducer would just update with whatever `action.group` was.
    console.warn("Attempted to select an invalid group:", group);
    return group; // Or perhaps throw an error / return rejectWithValue
  }
);

/**
 * Resets the current group, clears it from localStorage, and re-fetches groups.
 */
export const resetGroup = createAsyncThunk(
  "groups/resetGroup",
  async (_, { dispatch }) => {
    localStorage.removeItem('currentGroup');
    localStorage.removeItem('currentBlock');
    dispatch(getGroups()); // Re-fetch groups after resetting
    // No specific payload needed for the reducer, it will set currentGroup to null.
  }
);

// --- Slice Definition ---
const groupsSlice = createSlice({
  name: "groups",
  initialState,
  reducers: {
    // Potentially add direct reducers here if needed for actions not covered by thunks
    // e.g., a synchronous update to currentGroup if not using the selectGroup thunk for all cases.
  },
  extraReducers: (builder) => {
    builder
    // getGroups lifecycle
    .addCase(getGroups.pending, (state) => {
      state.fetchingGroups = true;
      state.groupsFetchError = null;
    })
    .addCase(getGroups.fulfilled, (state, action) => {
      state.fetchingGroups = false;
      state.groups = action.payload;
      state.groupsFetchError = null;
    })
    .addCase(getGroups.rejected, (state, action) => {
      state.fetchingGroups = false;
      state.groupsFetchError = action.payload || action.error.message;
    })
    // selectGroup lifecycle
    .addCase(selectGroup.fulfilled, (state, action) => {
      state.currentGroup = action.payload;
    })
    // resetGroup lifecycle
    .addCase(resetGroup.fulfilled, (state) => {
      state.currentGroup = null;
    });
  },
});

// --- Export Reducer ---
export default groupsSlice.reducer;

// --- Selectors ---
// These selectors assume the slice is added to the root reducer under the key 'groups'
const selectGroupsState = (state) => state.groups;

export const selectAllGroups = (state) => selectGroupsState(state).groups;
export const selectFetchingGroups = (state) => selectGroupsState(state).fetchingGroups;
export const selectGroupsFetchError = (state) => selectGroupsState(state).groupsFetchError;
export const selectCurrentGroup = (state) => selectGroupsState(state).currentGroup;

export const selectCurrentGroupName = (state) => {
  const currentGroup = selectCurrentGroup(state);
  return currentGroup && currentGroup.name ? currentGroup.name : ""; //
};

export const selectCurrentGroupId = (state) => {
  const currentGroup = selectCurrentGroup(state);
  return currentGroup && currentGroup.id ? currentGroup.id : 0; //
};

export const selectCurrentGroupLogoUrl = (state) => {
  const currentGroup = selectCurrentGroup(state);
  return currentGroup && currentGroup.logo_url ? currentGroup.logo_url : ""; //
};

export const selectInHouseResource = (state) => {
  const currentGroup = selectCurrentGroup(state);
  return currentGroup?.contacts?.find((contact) => contact.role === "primary"); //
};